/** @jsxImportSource @emotion/react */

import { ChangeEvent, FC } from 'react'
import { Field, useForm, useFormState } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import { OnChange } from 'react-final-form-listeners'
import TableButtons from 'components/TableButtons'
import { InputField } from 'components/Input'
import { SelectField } from 'components/Select'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { validateRequired } from 'utils/validation'
import { FormState } from '../consts'
import * as formClasses from '../styles'
import * as optionClasses from '../AnswersFieldArray/styles'
import {
  createTextCompletionBlank,
  createTextCompletionOption,
  TEXT_COMPLETION_BLANK_COUNT_OPTIONS,
  TEXT_COMPLETION_BLANK_LABELS,
  TEXT_COMPLETION_FIXED_OPTIONS,
  TEXT_COMPLETION_MAX_OPTIONS,
  TEXT_COMPLETION_MIN_OPTIONS,
  TextCompletionBlank,
  TextCompletionOption,
} from './helpers'
import * as classes from './styles'

const normalizeOptionsToFixedCount = (options: TextCompletionOption[] = []): TextCompletionOption[] => {
  const next = options.slice(0, TEXT_COMPLETION_FIXED_OPTIONS)

  while (next.length < TEXT_COMPLETION_FIXED_OPTIONS) {
    next.push(createTextCompletionOption(next.length === 0))
  }

  const firstCorrectIndex = next.findIndex(({ isCorrect }) => isCorrect)

  return next.map((option, index) => ({
    ...option,
    isCorrect: index === (firstCorrectIndex >= 0 ? firstCorrectIndex : 0),
  }))
}

const resizeBlanks = (blanks: TextCompletionBlank[], count: number): TextCompletionBlank[] => {
  if (count === blanks.length) {
    return blanks
  }

  if (count > blanks.length) {
    const next = blanks.map((blank, index) =>
      blanks.length === 1 && index === 0 ? { ...blank, options: normalizeOptionsToFixedCount(blank.options) } : blank
    )

    while (next.length < count) {
      next.push(createTextCompletionBlank())
    }

    return next
  }

  return blanks.slice(0, count)
}

const validateBlanks = (blanks: FormState['textCompletionAnswer']) => {
  if (!blanks?.length) {
    return 'Should have at least 1 blank'
  }

  const isSingleBlank = blanks.length === 1

  for (let index = 0; index < blanks.length; index++) {
    const blank = blanks[index]
    const options = blank.options ?? []
    const blankLabel = TEXT_COMPLETION_BLANK_LABELS[index]

    if (isSingleBlank && options.length < TEXT_COMPLETION_MIN_OPTIONS) {
      return `Should be at least ${TEXT_COMPLETION_MIN_OPTIONS} options`
    }

    if (isSingleBlank && options.length > TEXT_COMPLETION_MAX_OPTIONS) {
      return `Should be at most ${TEXT_COMPLETION_MAX_OPTIONS} options`
    }

    if (!isSingleBlank && options.length !== TEXT_COMPLETION_FIXED_OPTIONS) {
      return `Blank ${blankLabel} should have ${TEXT_COMPLETION_FIXED_OPTIONS} options`
    }

    if (options.filter(({ isCorrect }) => isCorrect).length !== 1) {
      return `Blank ${blankLabel} must have exactly one correct option`
    }
  }

  return undefined
}

type Props = {
  onAnswerChange: () => void
}

export const TextCompletionAnswers: FC<Props> = ({ onAnswerChange }) => {
  const form = useForm<FormState, Partial<FormState>>()
  const { values } = useFormState<FormState, Partial<FormState>>()
  const blanksCount = values.textCompletionAnswer?.length || 1
  const canEditOptions = blanksCount === 1

  return (
    <>
      <div css={formClasses.title}>Option Choices</div>
      <OnChange name="textCompletionAnswer">{() => onAnswerChange()}</OnChange>
      <SelectField<(typeof TEXT_COMPLETION_BLANK_COUNT_OPTIONS)[number]>
        name="textCompletionBlanksCount"
        items={TEXT_COMPLETION_BLANK_COUNT_OPTIONS}
        label="Number of Blanks"
        isNotNullable
        fullWidth
        css={classes.blanksCount}
      />
      <OnChange name="textCompletionBlanksCount">
        {(option: (typeof TEXT_COMPLETION_BLANK_COUNT_OPTIONS)[number] | undefined) => {
          const count = Number(option?.id)
          const currentBlanks = form.getState().values.textCompletionAnswer ?? []

          if (!count || count === currentBlanks.length) {
            return
          }

          form.change('textCompletionAnswer', resizeBlanks(currentBlanks, count))
        }}
      </OnChange>
      <FieldArray name="textCompletionAnswer" validate={validateBlanks}>
        {({ fields, meta }) => {
          const error = Array.isArray(meta.error) ? meta.error[0] : meta.error

          return (
            <>
              <div css={optionClasses.wrapper}>
                {fields.map((blankField, blankIndex) => (
                  <div key={blankField} css={optionClasses.item}>
                    <div css={classes.blankTitle}>Blank {TEXT_COMPLETION_BLANK_LABELS[blankIndex]}</div>
                    <FieldArray name={`${blankField}.options`}>
                      {({ fields: optionFields }) => (
                        <>
                          {optionFields.map((optionField, optionIndex) => (
                            <div key={optionField} css={classes.option}>
                              <div css={optionClasses.itemHeader}>
                                <Field name={`${optionField}.isCorrect`} type="checkbox">
                                  {({ input }) => (
                                    <FormControlLabel
                                      label={`Correct Option ${optionIndex + 1}`}
                                      control={
                                        <Checkbox
                                          {...input}
                                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                            input.onChange(event)

                                            if (event.target.checked) {
                                              optionFields.forEach((_, index) => {
                                                if (index !== optionIndex) {
                                                  optionFields.update(index, {
                                                    ...optionFields.value[index],
                                                    isCorrect: false,
                                                  })
                                                }
                                              })
                                            }
                                          }}
                                        />
                                      }
                                      css={optionClasses.checkbox}
                                    />
                                  )}
                                </Field>
                                {canEditOptions && (
                                  <Button
                                    variant="outlined"
                                    color="red"
                                    size="small"
                                    onClick={() => optionFields.remove(optionIndex)}
                                    disabled={(optionFields.length ?? 0) <= TEXT_COMPLETION_MIN_OPTIONS}
                                    css={optionClasses.removeButton}
                                  >
                                    <TrashIcon />
                                  </Button>
                                )}
                              </div>
                              <InputField
                                name={`${optionField}.value`}
                                validate={validateRequired}
                                placeholder={`Enter Option ${optionIndex + 1}`}
                                fullWidth
                              />
                            </div>
                          ))}
                          {canEditOptions && (
                            <TableButtons
                              itemName="New Option"
                              onAddItem={() => optionFields.push(createTextCompletionOption())}
                              disabled={(optionFields.length ?? 0) >= TEXT_COMPLETION_MAX_OPTIONS}
                            />
                          )}
                        </>
                      )}
                    </FieldArray>
                  </div>
                ))}
              </div>
              {Boolean(meta.touched && error) && <FormHelperText error>{String(error)}</FormHelperText>}
            </>
          )
        }}
      </FieldArray>
    </>
  )
}
